import h from 'hyperscript';
import { html, render } from 'lit-html';

import chevronLeftIcon from '@disy/cadenza-icons/chevron-left.svg';
import { assertNonNullable, requireNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { formatDuration } from '@disy/lib/format';
import { icon } from '@disy/lib/icon';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { Alert } from '@disy/ui/alert';
import { createFormGroup } from '@disy/ui/form-group';

import { createAutoToggle } from 'ui/auto-toggle/auto-toggle';
import type { AutoFieldset } from 'ui/fieldset/auto-fieldset';
import 'ui/help-tooltip/help-tooltip';
import { createResourceLabel } from 'ui/resource-label/resource-label';
import { Select } from 'ui/select/select';
import { Switch } from 'ui/switch/switch';

import { Command } from 'cadenza/api-client/workbook-commands';
import { getConfiguration } from 'cadenza/config/configuration';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { getBackgroundColorPalette } from 'cadenza/config/color-config';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { AnalysisContextVisibilityMode } from 'cadenza/workbook/model/analysis-context-visibility-mode.gen';
import { WorksheetLinearLayoutViewOrder } from 'cadenza/workbook/model/worksheet-linear-layout-view-order.gen';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key.gen';
import type { QueryExecutionStrategyType } from 'cadenza/workbook/query-execution-strategy-type';
import { QUERY_EXECUTION_STRATEGY_TYPE } from 'cadenza/workbook/query-execution-strategy-type';
import { createAutoControlledColorPicker } from 'cadenza/workbook/ui/color-picker-auto/color-picker-auto';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getActiveWorksheet,
  getActiveWorksheetViewIds,
  getAttributeById,
  getDataSourceByObjecttypeId,
  getDataViewsByViewId,
  getWorksheetBackgroundColor,
  getWorksheetById,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { DesignerSettingsPanelContent } from 'cadenza/workbook/workbook-panel/settings/designer-settings-panel-content';
import type { LiveModeRefreshInterval } from 'cadenza/workbook/workbook-panel/settings/live-mode-refresh-interval';
import {
  getCurrentRefreshInterval,
  getDefaultRefreshInterval,
} from 'cadenza/workbook/workbook-panel/settings/live-mode-refresh-interval';
import type { ViewSettingsPanel } from 'cadenza/workbook/workbook-panel/settings/view-settings-panel/view-settings-panel';
import { ViewsOrderPanel } from 'cadenza/workbook/workbook-panel/settings/worksheet/views-order-panel/views-order-panel';
import type { Settings } from 'cadenza/workbook/workbook-settings-util';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import {
  calculateWorksheetViewBorders,
  getWorksheetBackgroundColorAuto,
  getWorksheetViewBordersAuto,
} from 'cadenza/workbook/worksheet/worksheet-util';

import './worksheet-settings-panel.css';
import i18n from './worksheet-settings-panel.properties';

const COMPONENT_NAME = 'd-worksheet-settings-panel';

export class WorksheetSettingsPanel extends HTMLElement implements DesignerSettingsPanelContent {
  #dataSourceList?: HTMLUListElement;
  #dataSourcesWarning?: Alert;
  #executionPeriodSelect?: Select<LiveModeRefreshInterval>;
  #orderOfViewsComponent?: HTMLElement;
  #tearDowns: Teardown[] = [];

  connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'd-stack-v', 'space-4');
    this.#render();

    this.#tearDowns.push(
      observeStore(getStore(), getActiveWorksheetViewIds, (viewIds: ViewId[]) => {
        if (this.#orderOfViewsComponent) {
          this.#orderOfViewsComponent.hidden = viewIds.length < 1;
        }
      }),
    );
  }

  disconnectedCallback() {
    this.#tearDowns = tearDown(this.#tearDowns);
  }

  #render() {
    const worksheet = getActiveWorksheet(getStore().getState());
    const backgroundColorComponent = createBackgroundColorComponent(worksheet);
    this.#orderOfViewsComponent = new ViewsOrderPanel(worksheet.id);
    const borderComponent = createBorderComponent(worksheet);
    const updateStrategyComponent = this.#createUpdateStrategyComponent(worksheet);
    const analysisContextComponent = createAnalysisContextVisibilityFieldset(worksheet);

    // use it
    const mode = WorksheetLinearLayoutViewOrder.values;
    mode.values();

    render(
      html`<button class="button button-link button-s" @click=${() => this.#goToWorksheetOverview()}>
          ${icon(chevronLeftIcon)} ${i18n('worksheetOverview')}
        </button>
        <h3 class="${COMPONENT_NAME}--subtitle">${i18n('subTitle')}</h3>
        <form-group
          ><label>${i18n('worksheetName.label')}</label
          ><input
            class="form-control"
            type="text"
            value="${worksheet.printName}"
            @keydown="${(event: KeyboardEvent) => updateWorksheetName(event, worksheet.id)}"
            @blur="${(event: FocusEvent) => updateWorksheetName(event, worksheet.id)}"
        /></form-group>
        ${backgroundColorComponent}
        <h2 class="${COMPONENT_NAME}--h2">${i18n('views.title')}</h2>
        ${this.#orderOfViewsComponent} ${borderComponent} ${updateStrategyComponent}
        <h2 class="${COMPONENT_NAME}--h2">${i18n('analysisContext.title')}</h2>
        ${analysisContextComponent}`,
      this,
    );
  }

  #createUpdateStrategyComponent(worksheet: WorksheetDto) {
    this.#executionPeriodSelect = createExecutionPeriodSelect(worksheet);
    this.#executionPeriodSelect.addEventListener('change', () => {
      this.#updateDataSourceList(worksheet.id);
      changeWorksheetSettings(worksheet.id, this.#getPeriodicalExecutionSettings());
    });
    this.#dataSourceList = document.createElement('ul');
    this.#dataSourceList.classList.add(`${COMPONENT_NAME}--data-source`);
    this.#dataSourcesWarning = createRefreshIntervalWarning(this.#dataSourceList);
    const refreshFormGroup = createFormGroup(i18n('interval.label'), this.#executionPeriodSelect);

    const checked = getShowLoadingIndicatorsOnViews(worksheet);
    const showLoadingIndicatorSwitch = new Switch({
      checked,
    });
    showLoadingIndicatorSwitch.addEventListener('change', () =>
      changeWorksheetSettings(worksheet.id, {
        [WorksheetSettingsKey.toPersistenceKey('SHOW_LOADING_INDICATORS_IN_LIVE_MODE')]:
          showLoadingIndicatorSwitch.checked,
      }),
    );
    const indicatorContent = h('label', [showLoadingIndicatorSwitch, i18n('interval.loadingIndicator.label')]);
    const loadingIndicatorFormGroup = createFormGroup(indicatorContent);

    const executionStrategySelect = new Select<QueryExecutionStrategyType>({
      data: QUERY_EXECUTION_STRATEGY_TYPE.map((t) => {
        return { data: t, text: i18n(`queryExecution.<${t}>`), id: t };
      }),
    });
    executionStrategySelect.selection = getExecutionStrategySetting(worksheet);
    if (getExecutionStrategySetting(worksheet) === 'periodical') {
      this.#updateDataSourceList(worksheet.id);
      loadingIndicatorFormGroup.hidden = false;
    } else {
      refreshFormGroup.hidden = true;
      loadingIndicatorFormGroup.hidden = true;
      this.#dataSourcesWarning.hidden = true;
    }
    executionStrategySelect.addEventListener('change', () => {
      const newStrategy = executionStrategySelect.selection[0].data;
      if (newStrategy === 'periodical') {
        refreshFormGroup.hidden = false;
        loadingIndicatorFormGroup.hidden = false;
        this.#updateDataSourceList(worksheet.id);
        changeWorksheetSettings(worksheet.id, this.#getPeriodicalExecutionSettings());
      } else {
        refreshFormGroup.hidden = true;
        loadingIndicatorFormGroup.hidden = true;
        this.#getDataSourcesWarning().hidden = true;
        changeWorksheetSettings(worksheet.id, {
          [WorksheetSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY')]: newStrategy,
        });
      }
    });

    return html`<form-group><label>${i18n('queryExecution.label')}</label>${executionStrategySelect}</form-group
      >${refreshFormGroup}${this.#dataSourcesWarning}${loadingIndicatorFormGroup}`;
  }

  #updateDataSourceList(worksheetId: WorksheetId) {
    const dataSources = getRelevantDataSources(worksheetId);
    const intervalInSeconds = this.#getExecutionPeriodSelect().selection[0].data!.milliseconds / 1000.0;
    this.#getDataSourcesWarning().hidden = !dataSources
      .filter((dataSource) => !!dataSource.maxCachingAgeSeconds)
      .some((dataSource) => dataSource.maxCachingAgeSeconds! > intervalInSeconds);
    this.#getDataSourceList().innerHTML = '';
    this.#getDataSourceList().append(
      ...dataSources
        .filter((dataSource) => dataSource.maxCachingAgeSeconds && dataSource.maxCachingAgeSeconds > intervalInSeconds)
        .sort((d1, d2) => d1.printName.localeCompare(d2.printName))
        .map((dataSource) =>
          h<HTMLLIElement>('li', [
            createResourceLabel('datasource', dataSource.printName),
            ` (${dataSource.flavor}): ${dataSource.maxCachingAgeSeconds}s`,
          ]),
        ),
    );
  }

  #getPeriodicalExecutionSettings(): Settings {
    const interval = this.#getExecutionPeriodSelect().selection[0].data!.milliseconds;
    return {
      [WorksheetSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY')]: 'periodical',
      [WorksheetSettingsKey.toPersistenceKey('PERIODICAL_QUERY_EXECUTION_INTERVAL')]: interval,
    };
  }

  #goToWorksheetOverview() {
    const viewSettingsPanel = this.closest<ViewSettingsPanel>('.d-view-settings-panel');
    assertNonNullable(viewSettingsPanel);
    viewSettingsPanel.updatePanelContent();
  }

  #getDataSourceList() {
    return requireNonNullable(this.#dataSourceList);
  }

  #getDataSourcesWarning() {
    return requireNonNullable(this.#dataSourcesWarning);
  }

  #getExecutionPeriodSelect() {
    return requireNonNullable(this.#executionPeriodSelect);
  }

  get settingsPanelTitle() {
    return i18n('panel.title');
  }
}

customElements.define(COMPONENT_NAME, WorksheetSettingsPanel);

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: WorksheetSettingsPanel;
  }
}

function createBackgroundColorComponent(worksheet: WorksheetDto) {
  const backgroundColorAuto = getWorksheetBackgroundColorAuto(worksheet);
  const backgroundColor = getWorksheetBackgroundColor(getStore().getState(), {
    worksheetId: worksheet.id,
  });
  const currentUserColor = backgroundColorAuto ? undefined : backgroundColor;
  const defaultColor = getWorksheetBackgroundColor(getStore().getState(), {
    worksheetId: worksheet.id,
    autoOnly: true,
  });
  return createAutoControlledColorPicker({
    colorPalette: getBackgroundColorPalette(),
    currentUserColor,
    defaultColor,
    handleColorPickerChange: (newColor) =>
      changeWorksheetSettings(worksheet.id, {
        [WorksheetSettingsKey.toPersistenceKey('WORKSHEET_BACKGROUND_COLOR')]: newColor,
      }),
    handleAutoChange: (newAuto) =>
      changeWorksheetSettings(worksheet.id, {
        [WorksheetSettingsKey.toPersistenceKey('WORKSHEET_BACKGROUND_COLOR')]: newAuto ? null : defaultColor,
      }),
    label: i18n('backgroundColor.label'),
  });
}

function createBorderComponent(worksheet: WorksheetDto) {
  const calculatedBorders = calculateWorksheetViewBorders(worksheet.id, getStore().getState());
  const borderToggle = new Switch({ checked: calculatedBorders });
  const fieldSet = h<AutoFieldset>(
    'd-auto-fieldset',
    {
      auto: getWorksheetViewBordersAuto(worksheet),
      'onchange:auto': () => {
        changeWorksheetSettings(worksheet.id, {
          [WorksheetSettingsKey.toPersistenceKey('WORKSHEET_VIEW_BORDERS')]: fieldSet.auto ? null : calculatedBorders,
        });
        borderToggle.checked = calculatedBorders;
      },
    },
    [
      h('label.d-legacy-fieldset--legend', i18n('border.title')),
      h('form-group', [h(`label.${COMPONENT_NAME}--border-label`, [borderToggle, i18n('border.label')])]),
    ],
  );
  on(borderToggle, 'change', () => {
    changeWorksheetSettings(worksheet.id, {
      [WorksheetSettingsKey.toPersistenceKey('WORKSHEET_VIEW_BORDERS')]: borderToggle.checked,
    });
    fieldSet.auto = false;
  });
  return fieldSet;
}

function createExecutionPeriodSelect(worksheet: WorksheetDto) {
  const refreshIntervals = getConfiguration('workbook').liveMode.refreshIntervals;
  const selectedRefreshInterval =
    getCurrentRefreshInterval(worksheet, refreshIntervals) ?? getDefaultRefreshInterval(refreshIntervals);
  return new Select<LiveModeRefreshInterval>({
    styleClass: 'd-refresh-period',
    data: refreshIntervals.map((period) => {
      return {
        id: `${period.milliseconds}-${period.isDefault}`,
        data: period,
        text:
          formatDuration(period.milliseconds, { includeMilliseconds: true }) +
          (period.isDefault ? ` ${i18n('interval.default')}` : ''),
        selected: selectedRefreshInterval?.milliseconds === period.milliseconds,
      };
    }),
  });
}

function createRefreshIntervalWarning(dataSourcesList: HTMLUListElement) {
  const refreshIntervalWarning = new Alert();
  refreshIntervalWarning.classList.add('refresh-interval-warning', 'd-alert-small');
  refreshIntervalWarning.type = 'warning';
  refreshIntervalWarning.append(i18n('interval.warning.top'));
  refreshIntervalWarning.append(dataSourcesList);
  refreshIntervalWarning.append(i18n('interval.warning.bottom'));
  return refreshIntervalWarning;
}

function createAnalysisContextVisibilityFieldset(worksheet: WorksheetDto): AutoFieldset {
  const analysisContextVisibility = getSetting(
    worksheet,
    WorksheetSettingsKey.toPersistenceKey('ANALYSIS_CONTEXT_VISIBILITY'),
    'auto',
  );
  const updateSettings = () => {
    changeWorksheetSettings(worksheet.id, {
      [WorksheetSettingsKey.toPersistenceKey('ANALYSIS_CONTEXT_VISIBILITY')]:
        autoToggle.pressed ? 'auto' : visibilitySelect.selection[0].data!,
    });
  };
  const autoToggle = createAutoToggle({
    pressed: AnalysisContextVisibilityMode.fromPersistenceKey(analysisContextVisibility) === 'AUTO',
    onclick: () => {
      visibilitySelect.disabled = autoToggle.pressed;
      updateSettings();
    },
  });
  autoToggle.slot = 'label-aside';
  autoToggle.style.visibility = 'visible';
  autoToggle.classList.add('form-group-control');
  const visibilitySelect = createAnalysisContextVisibilitySelect(analysisContextVisibility);
  visibilitySelect.disabled = autoToggle.pressed;
  visibilitySelect.addEventListener('change', () => updateSettings());
  return h('form-group', [
    h('label', i18n('analysisContext.label')),
    h('d-help-tooltip', {
      'data-topic-id': 'worksheetSettings.analysisContext.auto',
      slot: 'label-buttons',
      style: { visibility: 'visible' },
    }),
    autoToggle,
    visibilitySelect,
  ]);
}

function createAnalysisContextVisibilitySelect(analysisContextVisibility: string) {
  return new Select<string>({
    data: AnalysisContextVisibilityMode.values
      .filter((mode) => mode !== 'AUTO')
      .map((mode) => ({
        text: AnalysisContextVisibilityMode.i18n(mode),
        id: mode,
        data: AnalysisContextVisibilityMode.toPersistenceKey(mode),
        selected: AnalysisContextVisibilityMode.fromPersistenceKey(analysisContextVisibility) === mode,
      })),
    styleClass: `${COMPONENT_NAME}--analysis-display`,
  });
}

function changeWorksheetSettings(worksheetId: WorksheetId, settings: Settings) {
  getStore().dispatch(executeCommand(Command.CHANGE_WORKSHEET_SETTINGS({ worksheetId, settings })));
}

function updateWorksheetName(event: FocusEvent | KeyboardEvent, worksheetId: WorksheetId) {
  if (
    (event instanceof FocusEvent && event.type === 'blur') ||
    (event instanceof KeyboardEvent && event.code === 'Enter')
  ) {
    const oldName = getActiveWorksheet(getStore().getState()).printName;
    const input = event.target as HTMLInputElement;
    const newName = input.value.trim();
    if (!newName || oldName === newName) {
      input.value = oldName;
      return;
    }
    getStore().dispatch(executeCommand(Command.RENAME_WORKSHEET({ worksheetId, newName })));
  }
}

function getExecutionStrategySetting(worksheet: WorksheetDto) {
  return getSetting<QueryExecutionStrategyType>(
    worksheet,
    WorksheetSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY'),
  );
}

function getRelevantDataSources(worksheetId: WorksheetId) {
  const state = getStore().getState();
  const objecttypeIds = new Set(
    getWorksheetById(state, worksheetId)
      .views.flatMap((viewId) => getDataViewsByViewId(state, viewId))
      .flatMap((dataView) => Object.values(dataView.attributes).flat())
      .map((activeAttributeId) => getAttributeById(state, activeAttributeId).objecttypeId),
  );
  const dataSources = new Set(
    [...objecttypeIds]
      .map((objecttypeId) => getDataSourceByObjecttypeId(state, objecttypeId))
      .filter((dataSource) => dataSource.maxCachingAgeSeconds),
  );
  return [...dataSources];
}

function getShowLoadingIndicatorsOnViews(worksheet: WorksheetDto): boolean {
  return getSetting<boolean>(
    worksheet,
    WorksheetSettingsKey.toPersistenceKey('SHOW_LOADING_INDICATORS_IN_LIVE_MODE'),
    true,
  );
}
